import { useMemo } from 'react';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { AdminFormPage } from '../../components/AdminFormPage';
import { getTenantFormConfig } from '../../configs/tenantFormConfig';
import { tenantsService } from '../../api/tenantsService';
import { parseJsonField } from '../../utils/parseJsonField';
import type { StoreTenantRequest, UpdateTenantRequest } from '../../api/tenantsService';

interface TenantParams {
  slug?: string;
}

export function TenantFormPage() {
  const formPage = useAdminFormPage<unknown, TenantParams>({
    entityName: 'tenant',
    getConfig: (mode, params) => getTenantFormConfig(mode, params.slug),
    getEditId: (params) => params.slug,
    queryKey: (params) => ['admin', 'tenants', params.slug],
    fetchFn: (params) => {
      if (!params.slug) {
        return Promise.reject(new Error('Tenant slug is required'));
      }
      return tenantsService.get(params.slug);
    },
    mapToInitialValues: (entity) => {
      const tenant = entity as Record<string, unknown>;
      return {
        slug: tenant.slug,
        name: tenant.name,
        domain: (tenant.domain as string) ?? '',
        settings: tenant.settings ? JSON.stringify(tenant.settings, null, 2) : '',
        is_active: tenant.is_active,
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const settingsValue = parseJsonField(payload.settings);
      const cleanedPayload = {
        ...payload,
        settings: settingsValue,
        domain: (payload.domain as string) || null,
      };
      if (isEdit && params.slug) {
        return tenantsService.update(params.slug, cleanedPayload as UpdateTenantRequest);
      }
      return tenantsService.create(cleanedPayload as StoreTenantRequest);
    },
    invalidateKey: () => ['admin', 'tenants'],
    navigateBackPath: () => '/admin/tenants',
  });

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Tenants', href: '/admin/tenants' },
    { label: formPage.isEdit ? 'Edit Tenant' : 'Create Tenant' },
  ], [formPage.isEdit]);

  const errorBreadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Tenants', href: '/admin/tenants' },
    { label: 'Edit' },
  ], []);

  return (
    <AdminFormPage
      formPage={formPage}
      entityLabel="Tenant"
      breadcrumbs={breadcrumbs}
      errorBreadcrumbs={errorBreadcrumbs}
      genericFetchErrorMessage="An unexpected error occurred while loading the tenant."
    />
  );
}
